import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {observer} from 'mobx-react';
import colors from 'const/style/colors';
import config from 'config';
import VideoPreview from "../components/PresentationPlay/VideoPreview";
import SlideThumbList, {ISlideThumbListHandle} from "../components/PresentationPlay/SlideThumbList";
import PresentationHeader from "../components/PresentationPlay/PresentationHeader";
import VideoPlayBar from "../components/PresentationPlay/VideoPlayBar";
import PresentationViewer from "../components/PresentationPlay/PresentationViewer";
import IPTInformation, {UserStatusType} from "../interfaces/IPTInformation";
import CustomAlert from "../components/PresentationPlay/Alert";
import {Spinner} from "../components/Spinner";
import ProgressBar from "../components/ProgressBar";

const LEFT_FRAME_WIDTH = 175;
const DEFAULT_IMAGE_HEIGHT = 132;

const StyledVideoPreview: typeof VideoPreview = styled(VideoPreview)`
    // ${(props) => !(props as any)['data-minHeight'] && 'display: none;'}
    min-height: ${(props) => (props as any)['data-minHeight']}px;
    border: 1px solid #D8D8D8;
`;

const StyledSlideThumbList: typeof SlideThumbList = styled(SlideThumbList)`
  flex: 1;
`;

const Frame = styled.div`
    width:100%;
    height:100%;
    display:flex;
    flex-grow:row;
    justify-content:flex-start;
    align-items:stretch;
    
    //** layout */
    .frame-left {
        background:${colors.CECECEC};
        position:relative;
        width:${LEFT_FRAME_WIDTH}px;
        display:flex;
        flex-direction:column;
        justify-content:flex-start;
        align-items:stretch;
    }
    .frame-content {
        position:relative;
        flex:1;
        display:flex;
        background:${colors.CECECEC};
        flex-direction:column;
        justify-content:flex-start;
        align-items:stretch;
        padding-left:24px;
        padding-right:24px;
        padding-bottom:30px;
        padding-top:23px;
        min-width: 1100px;
    }
`;

interface IPresentationPlayProps {
    ptInformation: IPTInformation;
}

export const Presentation: FC<IPresentationPlayProps> = ({ptInformation}) => {
    const refVideo = useRef<HTMLVideoElement>(null);
    const refSlideThumbListHandle = useRef<ISlideThumbListHandle>(null);
    const [duration, setDuration] = useState<number>(0);
    const [playbackMs, setPlaybackMs] = useState<number>(0);

    useEffect(() => {
        ptInformation.init();
    }, []);

    useEffect(() => {
        if(!refVideo.current?.paused){
            if(playbackMs < 1000) {
                ptInformation.tempSlide = ptInformation.currentSlide;
            }
        }
    }, [refVideo.current?.paused]);

    useEffect(() => {
        if (playbackMs !== 0) {
            setCurrentMarkerIndex(playbackMs);
            const slideIndex = ptInformation.ptSlideEvents[ptInformation.currentMarkerIndex].slideIndex + 1;
            if (ptInformation.currentSlide !== slideIndex) {
                moveSlideWithThumbScroll(slideIndex)
            }
        }
    }, [playbackMs]);

    const moveSlide = (idx: number) => {
        ptInformation.currentSlide = idx;
        // if (presentationPlay.currentSlide === presentationPlay.tempSlide) {
        //     presentationPlay.tempSlide = 0;
        // }
    };

    const moveTempSlide = (idx: number) => {
        ptInformation.tempSlide = idx;
        // if (presentationPlay.currentSlide === presentationPlay.tempSlide) {
        //     presentationPlay.tempSlide = 0;
        // }
    };

    const moveSlideWithThumbScroll = (slideIndex: number) => {
        if (0 < slideIndex && slideIndex <= ptInformation.ptImages.length) {
            ptInformation.tempSlide = slideIndex;
            moveSlide(slideIndex);
            refSlideThumbListHandle.current?.scrollToIndex(slideIndex);
        }
    };

    const handleVideoTimeUpdate = (ms: number) => {
        setPlaybackMs(ms);
    };

    const handleVideoDurationUpdate = (ms: number) => {
        setDuration(ms);
    };

    const videoMinHeight = useMemo(() => {
        if (!refVideo.current) {
            return DEFAULT_IMAGE_HEIGHT;
        }
        const videoRatio = ((refVideo.current.videoHeight || 0) / (refVideo.current.videoWidth || 1));
        if(videoRatio === 0){
            return DEFAULT_IMAGE_HEIGHT;
        }
        console.log("videoRatio", refVideo.current.videoHeight, LEFT_FRAME_WIDTH);
        return videoRatio * LEFT_FRAME_WIDTH;
    }, [refVideo.current, refVideo.current?.videoWidth, refVideo.current?.videoHeight]);

    const setCurrentMarkerIndex = (ms: number) => {
        let tempIndex = 0;
        for (const ptSlideEvent of ptInformation.ptSlideEvents) {
            if (ms < ptSlideEvent.indexTime) {
                break;
            }
            tempIndex++;
        }
        ptInformation.currentMarkerIndex = tempIndex - 1;
    };

    return (
        <Frame>
            <div className='frame-left'>
                <StyledVideoPreview
                    key={ptInformation.ptVideo.id}
                    ref={refVideo}
                    isBlind={ptInformation.isBlind}
                    data-minHeight={videoMinHeight}
                    sourceSrc={ptInformation.ptVideo.fileUrl}
                    // sourceSrc={`${config.hostRes}api/file/${ptInformation.ptVideo.id}`}
                    sourceType="video/mp4"
                    onTimeUpdate={handleVideoTimeUpdate}
                    onDurationChange={handleVideoDurationUpdate}/>
                <StyledSlideThumbList
                    ref={refSlideThumbListHandle}
                    images={ptInformation.ptImages}
                    onClickSlide={(slide, index) => moveTempSlide(index + 1)}
                    srcURLPrefix={config.hostRes}
                    playingIndex={ptInformation.currentSlide - 1}
                    tempSelectedIndex={ptInformation.tempSlide - 1}
                    // downloadUrl={ptInformation.ptPdf.fileUrl}
                    downloadUrl={`${config.hostRes}api/file/${ptInformation.ptPdf.id}`}
                    fileName={ptInformation.ptPdf.fileName}
                />
            </div>
            <div className='frame-content'>
                <PresentationHeader ptInformation={ptInformation}/>
                {/*{refVideo.current && (*/}
                    <>
                        <PresentationViewer videoDOM={refVideo.current || undefined}
                                            images={ptInformation.ptImages}
                                            tempSlideIndex={ptInformation.tempSlide}
                                            currentSlideIndex={ptInformation.currentSlide}/>
                        {refVideo.current && (
                            <VideoPlayBar currentMsTime={playbackMs}
                                          videoDOM={refVideo.current}
                                          duration={duration}
                                          slideEvents={ptInformation.ptSlideEvents}
                                          currentMarkerIndex={ptInformation.currentMarkerIndex}
                                          onCurrentTimeChange={currentTimeMS => {
                                              setPlaybackMs(currentTimeMS * 1000);
                                          }}/>)
                        }
                    </>
                {/*)}*/}
            </div>
            {
                !(ptInformation.isLoaded && ptInformation.isConvertPDF) ?
                // <Spinner text={ptInformation.loadingPage+" / "+ptInformation.totalPage}/> :
                    <ProgressBar leftText={"PDF 로딩중"}
                                 rightText={ptInformation.loadingPage+"/"+ptInformation.totalPage}
                                 percentage={Math.floor((ptInformation.loadingPage/ptInformation.totalPage)*100)}/> :
                ptInformation.user.userStatusType === UserStatusType.ENCODING_COMPLETED ||
                  <CustomAlert>
                    응시 영상의 확장자 변환을 처리 중입니다.
                    <br/>
                    변환이 완료되기 전까지는
                    <br/>
                    영상을 처음부터 끝까지 조회한 후에
                    <br/>
                    각 슬라이드의 재생시간과
                    <br/>
                    이동 마커를 확인할 수 있습니다.
                  </CustomAlert>
            }
        </Frame>
    )
};

export default (observer(Presentation));
